import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * @typedef {object} FitChartRefs
 * @property {HTMLTableElement} [table]
 */

/**
 * Highlights the size column that matches the selected variant, and the
 * column under the pointer. Shared by `<fit-chart>` and `<scales-chart>`.
 *
 * @extends {Component<FitChartRefs>}
 */
class FitChart extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLElement | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();

    const table = this.refs.table;
    if (!(table instanceof HTMLTableElement)) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    table.addEventListener('pointerover', this.#onPointerOver, { signal });
    table.addEventListener('pointerout', this.#onPointerOut, { signal });
    table.addEventListener('click', this.#onClick, { signal });

    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    this.#applyVariant(this.#readCurrentVariant());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#section = null;
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    this.#applyVariant(event.detail.resource);
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerOver = (event) => {
    if (event.pointerType === 'touch') return;

    const cell = this.#cellFromEvent(event);
    if (!cell) return;

    this.#clearClass('product-sizechart__highlight');
    this.#highlightColumn(cell, 'product-sizechart__highlight');
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerOut = (event) => {
    if (event.pointerType === 'touch') return;

    const next = event.relatedTarget;
    if (next instanceof Node && this.refs.table?.contains(next)) return;

    this.#clearClass('product-sizechart__highlight');
  };

  /**
   * Column toggle for coarse pointers. Fine pointers already highlight on hover.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const cell = this.#cellFromEvent(event);
    if (!cell) return;

    const column = this.#columnCells(cell);
    const isOn = column.length > 0 && column.every((item) => item.classList.contains('product-sizechart__highlight'));

    this.#clearClass('product-sizechart__highlight');

    if (!isOn) {
      for (const item of column) item.classList.add('product-sizechart__highlight');
    }
  };

  /**
   * @param {Event} event
   * @returns {HTMLTableCellElement | null}
   */
  #cellFromEvent(event) {
    if (!(event.target instanceof Element)) return null;

    const cell = event.target.closest('th, td');
    if (!(cell instanceof HTMLTableCellElement)) return null;
    if (!this.refs.table?.contains(cell)) return null;

    return cell;
  }

  /**
   * @param {HTMLTableCellElement} cell
   * @param {string} className
   */
  #highlightColumn(cell, className) {
    for (const item of this.#columnCells(cell)) {
      item.classList.add(className);
    }
  }

  /**
   * @param {HTMLTableCellElement} cell
   * @returns {HTMLTableCellElement[]}
   */
  #columnCells(cell) {
    const table = this.refs.table;
    if (!(table instanceof HTMLTableElement)) return [];

    const index = cell.cellIndex;
    /** @type {HTMLTableCellElement[]} */
    const cells = [];

    for (const row of table.rows) {
      const columnCell = row.cells[index];
      if (columnCell) cells.push(columnCell);
    }

    return cells;
  }

  /**
   * @param {string} className
   */
  #clearClass(className) {
    const table = this.refs.table;
    if (!(table instanceof HTMLTableElement)) return;

    for (const cell of table.querySelectorAll(`.${className}`)) {
      cell.classList.remove(className);
    }
  }

  /**
   * @returns {object | null}
   */
  #readCurrentVariant() {
    const productId = this.dataset.productId;
    const picker = productId
      ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
      : document.querySelector('variant-picker[data-template-product-match="true"]');
    const raw = picker?.querySelector('script[type="application/json"]')?.textContent;

    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * @param {object | null | undefined} variant
   */
  #applyVariant(variant) {
    const table = this.refs.table;
    if (!(table instanceof HTMLTableElement)) return;

    this.#clearClass('product-sizechart__selected');

    const sizes = new Set(this.#sizeValues(variant));
    if (sizes.size === 0) return;

    const candidates = [
      ...table.querySelectorAll('thead [data-size]'),
      ...table.querySelectorAll('tbody tr[data-size]'),
    ];

    for (const node of candidates) {
      if (!(node instanceof HTMLElement)) continue;

      const size = node.dataset.size?.trim().toLowerCase();
      if (!size || !sizes.has(size)) continue;

      if (node instanceof HTMLTableRowElement) {
        node.classList.add('product-sizechart__selected');
        continue;
      }

      const column = node.getAttribute('data-column');
      if (!column) continue;

      for (const cell of table.querySelectorAll('[data-column]')) {
        if (cell.getAttribute('data-column') === column) {
          cell.classList.add('product-sizechart__selected');
        }
      }
    }
  }

  /**
   * @param {object | null | undefined} variant
   * @returns {string[]}
   */
  #sizeValues(variant) {
    if (!variant || typeof variant !== 'object') return [];

    /** @type {string[]} */
    const values = [];
    const options = /** @type {{ options?: unknown, option1?: unknown, option2?: unknown, option3?: unknown }} */ (
      variant
    );

    if (Array.isArray(options.options)) {
      for (const option of options.options) {
        if (option != null && option !== '') values.push(String(option));
      }
    }

    for (const key of ['option1', 'option2', 'option3']) {
      const value = options[key];
      if (value != null && value !== '') values.push(String(value));
    }

    return values.map((value) => value.trim().toLowerCase());
  }
}

if (!customElements.get('fit-chart')) {
  customElements.define('fit-chart', FitChart);
}

if (!customElements.get('scales-chart')) {
  customElements.define('scales-chart', FitChart);
}
